import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Clock, ArrowLeft, User, Linkedin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DOMPurify from "dompurify";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category: string;
  author_name: string;
  author_bio: string | null;
  author_image_url: string | null;
  featured_image_url: string | null;
  published_at: string;
  reading_time: number;
  meta_description: string;
  meta_keywords: string[] | null;
  view_count: number;
  
  // New SEO fields
  meta_title: string | null;
  canonical_url: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image_alt: string | null;
  og_image_width: number | null;
  og_image_height: number | null;
  article_tags: string[] | null;
  twitter_card: string | null;
}

interface RelatedPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  reading_time: number;
}

const categoryNames: Record<string, string> = {
  "cyber-essentials": "Cyber Essentials",
  "fractional-ciso": "Fractional CISO",
  "sme-security": "SME Security",
  "compliance": "Compliance",
};

const categoryColors: Record<string, string> = {
  "cyber-essentials": "bg-primary/10 text-primary",
  "fractional-ciso": "bg-secondary/10 text-secondary",
  "sme-security": "bg-accent/10 text-accent",
  "compliance": "bg-muted text-muted-foreground",
};

// Map internal category slugs to human-readable section names
const categoryToSection: Record<string, string> = {
  "cybersecurity-basics": "Cyber Security Basics",
  "threat-intelligence": "Threat Intelligence",
  "compliance-regulations": "Compliance & Regulations",
  "risk-management": "Risk Management",
  "security-strategy": "Security Strategy",
  "small-business-security": "Small Business Security",
};

const InsightPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (slug) {
      fetchPost();
    }
  }, [slug]);

  const fetchPost = async () => {
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .not("published_at", "is", null)
        .single();

      if (error) throw error;

      if (data) {
        setPost(data);
        fetchRelatedPosts(data.category, data.id);
        incrementViewCount(data.id);
      }
    } catch (error) {
      console.error("Error fetching blog post:", error);
      toast({
        title: "Error",
        description: "Failed to load blog post. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRelatedPosts = async (category: string, currentPostId: string) => {
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, reading_time")
        .eq("category", category)
        .eq("status", "published")
        .not("published_at", "is", null)
        .neq("id", currentPostId)
        .order("published_at", { ascending: false })
        .limit(3);

      if (error) throw error;
      setRelatedPosts(data || []);
    } catch (error) {
      console.error("Error fetching related posts:", error);
    }
  };

  const incrementViewCount = async (postId: string) => {
    try {
      await supabase
        .from("blog_posts")
        .update({ view_count: post ? post.view_count + 1 : 1 })
        .eq("id", postId);
    } catch (error) {
      console.error("Error incrementing view count:", error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <>
        <div className="min-h-screen flex flex-col bg-background">
          <Navbar />
          <main className="pt-24 pb-16 bg-secondary/30">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Skeleton className="h-12 w-3/4 mb-4" />
                <Skeleton className="h-6 w-1/2 mb-8" />
                <Skeleton className="h-96 w-full" />
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  if (!post) {
    return (
      <>
        <div className="min-h-screen flex flex-col bg-background">
          <Navbar />
          <main className="pt-24 pb-16 bg-secondary/30">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
              <p className="text-muted-foreground mb-8">
                The blog post you're looking for doesn't exist or has been removed.
              </p>
                <Link
                  to="/insights"
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Insights
                </Link>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.meta_description,
    "image": post.featured_image_url,
    "datePublished": post.published_at,
    "author": {
      "@type": "Person",
      "name": post.author_name
    },
    "publisher": {
      "@type": "Organization",
      "name": "4FOX Security",
      "logo": {
        "@type": "ImageObject",
        "url": "https://4fox.uk/4fox-logo-optimized.png"
      }
    }
  };

  return (
    <>
      <SEO
        title={post.meta_title || `${post.title} | 4FOX Security Insights`}
        description={post.meta_description}
        canonical={post.canonical_url || `https://www.4fox.uk/insights/${post.slug}`}
        keywords={post.meta_keywords || undefined}
        ogTitle={post.og_title || post.meta_title || post.title}
        ogDescription={post.og_description || post.meta_description}
        ogImage={post.featured_image_url || undefined}
        ogImageAlt={post.og_image_alt || `Featured image for ${post.title}`}
        ogImageWidth={post.og_image_width || 1200}
        ogImageHeight={post.og_image_height || 675}
        ogType="article"
        ogUrl={post.canonical_url || `https://www.4fox.uk/insights/${post.slug}`}
        articlePublishedTime={post.published_at}
        articleAuthor={post.author_name}
        articleSection={categoryToSection[post.category] || post.category}
        articleTags={post.article_tags || []}
        twitterCard={post.twitter_card || "summary_large_image"}
        twitterTitle={post.og_title || post.meta_title || post.title}
        twitterDescription={post.og_description || post.meta_description}
        twitterImage={post.featured_image_url || undefined}
        schema={schema}
      />
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        
        <main className="pt-24 pb-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <article className="max-w-4xl mx-auto">
              {/* Back Link */}
              <Link
                to="/insights"
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Insights
              </Link>

              {/* Article Header */}
              <header className="mb-8">
                <Badge className={`mb-4 ${categoryColors[post.category]}`}>
                  {categoryNames[post.category]}
                </Badge>
                <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
                  {post.title}
                </h1>
                
                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {post.author_name}
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDate(post.published_at)}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {post.reading_time} min read
                  </span>
                </div>
              </header>

              {/* Featured Image */}
              {post.featured_image_url && (
                <div className="mb-8 rounded-lg overflow-hidden bg-gradient-to-br from-primary/10 to-secondary/10 aspect-video">
                  <img
                    src={post.featured_image_url}
                    alt={post.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="w-full h-full flex items-center justify-center">
                            <div class="text-center p-8">
                              <svg class="w-20 h-20 mx-auto text-muted-foreground/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <p class="text-sm text-muted-foreground mt-3">Featured Image</p>
                            </div>
                          </div>
                        `;
                      }
                    }}
                  />
                </div>
              )}

              {/* Article Content */}
              <div
                className="prose prose-lg max-w-none mb-12 prose-headings:text-foreground prose-p:text-foreground prose-p:my-4 prose-strong:text-foreground prose-li:text-foreground prose-a:text-primary prose-img:max-w-full prose-img:h-auto prose-img:max-h-96 prose-img:rounded-lg prose-img:mx-auto"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(
                    post.content.replace(/<p><\/p>/g, '<div class="my-6"></div>'),
                    { ADD_TAGS: ['div'], ADD_ATTR: ['class'] }
                  ) 
                }}
              />

              {/* Author Bio */}
              {post.author_name === "Hazel McPherson" ? (
                <Card className="mb-12 bg-muted/50">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="relative h-20 w-20 rounded-full overflow-hidden bg-gradient-to-br from-primary/10 to-secondary/10 flex-shrink-0">
                        <img
                          src={post.author_image_url || "/team/hazel-mcpherson.jpg"}
                          alt="Hazel McPherson"
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg mb-1">by Hazel McPherson</CardTitle>
                        <p className="text-sm text-muted-foreground">
                          CISSP, C|CISO, ITIL Expert | MSc Cyber Security, MBA
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Award-winning cyber security leader with 20+ years protecting regulated organisations and critical infrastructure. Former UK InfoSec lead at Ayvens. Expert in governance, risk, and resilience. Co-founder of CSIDES.
                    </p>
                    <div className="flex gap-4 items-center">
                      <a
                        href="https://www.linkedin.com/in/hazelmcpherson/"
                        target="_blank"
                        rel="noopener noreferrer author"
                        className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <Linkedin className="h-4 w-4" />
                        Connect on LinkedIn
                      </a>
                      <Link
                        to="/about"
                        className="text-sm text-primary hover:underline"
                      >
                        Learn more about Hazel
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ) : post.author_bio ? (
                <Card className="mb-12 bg-muted/50">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      {post.author_image_url && (
                        <div className="relative h-16 w-16 rounded-full overflow-hidden bg-gradient-to-br from-primary/10 to-secondary/10 flex-shrink-0">
                          <img
                            src={post.author_image_url}
                            alt={post.author_name}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              const target = e.currentTarget;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `
                                  <div class="h-16 w-16 flex items-center justify-center">
                                    <svg class="w-8 h-8 text-muted-foreground/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                  </div>
                                `;
                              }
                            }}
                          />
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-lg">by {post.author_name}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{post.author_bio}</p>
                  </CardContent>
                </Card>
              ) : null}

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold mb-6 text-foreground">Related Articles</h2>
                  <div className="grid md:grid-cols-3 gap-6">
                    {relatedPosts.map((relatedPost) => (
                      <Link key={relatedPost.id} to={`/insights/${relatedPost.slug}`}>
                        <Card className="h-full hover:shadow-lg transition-shadow">
                          <CardHeader>
                            <CardTitle className="text-lg hover:text-primary transition-colors line-clamp-2">
                              {relatedPost.title}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                              {relatedPost.excerpt}
                            </p>
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {relatedPost.reading_time} min read
                            </span>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default InsightPost;
